import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import LogoFallback from "/img/logo.png";
import { Mail, Phone, MapPin,BookOpen  } from "lucide-react";
import axios from "axios";

export default function AdmitCard({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Download Admit Card | University of Creative Technology Chittagong",
}) {
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState("");
	const [error, setError] = useState("");
	const [formData, setFormData] = useState({
		mobile_number: "",
		verification_code: "",
	});
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};
	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");
		setSuccess("");

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				...formData, // include name, email, message
			};
			const response = await axios.post(
				`${import.meta.env.VITE_API_BASE_URL}/admit-card`,
				payload,
				{
				  headers: { "Content-Type": "application/json" },
				}
			);
			if (response.data?.responseCode === "00") {        
				const fileUrl = `${import.meta.env.VITE_API_SOURCE_URL}${response.data.responseMsg}`;
				forceDownload(fileUrl, "Admission-Form.pdf");
			} else {
				setError(response.data?.responseMsg || "Something went wrong.");
			}
		} catch (err) {
			setError(err.response?.data?.message || "Server error, please try later.");
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
	
	const forceDownload = (url, filename) => {
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", filename);
		document.body.appendChild(link);
		link.click();
		link.remove();
	};
	return (
	<>
	<div className="min-h-screen bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF] py-12">
      <div className="max-w-6xl mx-auto px-6">
        {/* Hero */}
        <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#6BCCF2] to-teal-50 p-8 shadow-xl">
          <div className="flex items-center gap-6">
            <div className="p-4 rounded-full bg-white/80 shadow-md">
              <BookOpen className="w-12 h-12 text-amber-700" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-[#014864]">Download Admit Card</h1>              
            </div>

            {/* decorative SVG leaf */}
            <svg className="absolute right-6 top-6 w-40 opacity-30" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path d="M42.2,-54.6C57.1,-49.2,72.6,-41.6,78.5,-29.1C84.5,-16.5,80.8,0.1,72.6,13.9C64.5,27.8,51.9,38.8,38.4,47.8C24.9,56.9,10.5,64.1,-3.1,67.6C-16.7,71.1,-33.4,70,-44.2,61.6C-55.1,53.2,-60.1,37.4,-63.2,22.9C-66.3,8.4,-67.4,-5.8,-63.9,-19.9C-60.4,-34,-52.3,-48,-38.6,-54.6C-24.9,-61.2,-12.4,-60.5,1.6,-62.3C15.6,-64.1,31.2,-68,42.2,-54.6Z" fill="#fff7ed" transform="translate(100 100)" />
            </svg>
          </div>
        </motion.header>
	   {/* Contact Form */}
      <section className="max-w-3xl mx-auto py-16 px-6">
        
		
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 space-y-6">
			{/* Success & Error Messages */}
			{success && <div className="mt-4 p-3 rounded-lg bg-green-100 text-green-800 border border-green-300">{success}</div>}
			{error && <div className="mt-4 p-3 rounded-lg bg-red-100 text-red-800 border border-red-300">{error}</div>}
          <div>
            <label className="block font-medium text-gray-700 mb-2">Mobile Number</label>
            <input
              type="text"
			  name="mobile_number"
              value={formData.mobile_number}
              onChange={handleChange}
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Mobile Number"
              required
            />
          </div>
          <div>
            <label className="block font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
			  name="verification_code"
              value={formData.verification_code}
              onChange={handleChange}
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Password"
              required
            />
          </div>
		  
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#014864] text-white font-semibold py-3 rounded-lg shadow hover:bg-[#016C94] transition"
          >
            {loading ? "Downloading..." : "Download Admission Form"}
          </button>
        </form>
      </section>
	  
	  </div>
	  {/* Contact Info */}
      <section className="bg-gradient-to-r from-[#6BCCF2] to-teal-50 text-[#014864] py-12">
        <div className="max-w-6xl mx-auto px-6 text-center space-y-4">
          <h3 className="text-2xl font-bold mb-4">Admission and Information Division</h3>
          <div className="flex flex-col md:flex-row justify-center gap-6">
            <div className="flex items-center gap-2">
              <MapPin size={20} /> <span>Chandgaon Police Station, 1084, Shah Amanat Bridge Connecting Road, Chittagong 4212</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={20} /> <span>+880 1707508080</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={20} /> <span>admission@uctc.edu.bd</span>
            </div>
          </div>
          <button className="mt-6 px-6 py-3 bg-white text-[#014864] font-semibold rounded-lg shadow hover:bg-gray-100">
            Request Appointment
          </button>
        </div>
      </section>
	  
	</div>
	</>
	);
}